import { Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { useNavigate } from 'react-router-dom';

// Social links configuration - add your links here when ready
const socialLinks = {
  facebook: null,  // e.g., 'https://facebook.com/cloudit'
  twitter: null,   // e.g., 'https://twitter.com/cloudit'
  linkedin: null,  // e.g., 'https://linkedin.com/company/cloudit'
  instagram: null  // e.g., 'https://instagram.com/cloudit'
};

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Check if any social links are configured
  const hasSocialLinks = Object.values(socialLinks).some(link => link !== null);

  return (
    <footer className="relative border-t border-[var(--border)]" style={{ background: 'var(--bg-dark)' }}>
      {/* Gradient Top Border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#3B82F6]/50 to-transparent"></div>

      <div className="max-w-[1400px] mx-auto px-[7.6923%] py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div>
            <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
              CLOUD{' '}
              <span className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">
                IT
              </span>
            </h3>
            <p className="text-[var(--text-muted)] mb-4 leading-relaxed text-sm">
              Building Intelligent Digital Futures
            </p>
            <p className="text-[var(--text-muted)] text-sm leading-relaxed opacity-70">
              Forward-thinking digital innovation company focused on delivering scalable, secure, and intelligent solutions.
            </p>
            
            {/* CTA Button */}
            <button 
              onClick={() => navigate('/signup')}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
            >
              Get Started
              <ArrowUpRight size={16} />
            </button>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[var(--text-primary)] font-semibold mb-5 text-base">Quick Links</h4>
            <ul className="space-y-3">
              {['Home', 'About Us', 'Services', 'Contact'].map((item, i) => (
                <li key={i}>
                  <button 
                    onClick={() => scrollToSection(item.toLowerCase().replace(' us', ''))} 
                    className="text-[var(--text-muted)] hover:text-[#3B82F6] transition-colors duration-300 text-sm flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6]/50 group-hover:bg-[#3B82F6] transition-colors"></span>
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-[var(--text-primary)] font-semibold mb-5 text-base">Our Services</h4>
            <ul className="space-y-3">
              {['Website Development', 'App Development', 'UI/UX Design', 'Agentic AI Solutions', 'Business Tools'].map((service, i) => (
                <li key={i} className="text-[var(--text-muted)] text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8B5CF6]/50"></span>
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-[var(--text-primary)] font-semibold mb-5 text-base">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#3B82F6]/10 border border-[#3B82F6]/30 flex items-center justify-center flex-shrink-0">
                  <Phone size={14} className="text-[#3B82F6]" />
                </div>
                <a href={`tel:${contactInfo.phone}`} className="text-[var(--text-muted)] hover:text-[#3B82F6] transition-colors duration-300 text-sm">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 flex items-center justify-center flex-shrink-0">
                  <Mail size={14} className="text-[#8B5CF6]" />
                </div>
                <a href="mailto:support@clouditllc.ai" className="text-[var(--text-muted)] hover:text-[#8B5CF6] transition-colors duration-300 text-sm break-all">
                  support@clouditllc.ai
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#F97316]/10 border border-[#F97316]/30 flex items-center justify-center flex-shrink-0">
                  <MapPin size={14} className="text-[#F97316]" />
                </div>
                <span className="text-[var(--text-muted)] text-sm">
                  {contactInfo.address}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[var(--border)] pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Copyright */}
            <div className="text-[var(--text-muted)] text-sm text-center md:text-left">
              <p>&copy; {currentYear} CLOUD IT. All rights reserved.</p>
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-6 text-xs">
              {['Privacy Policy', 'Terms of Service', 'Legal Disclaimer'].map((link, i) => (
                <span key={i} className="flex items-center gap-6">
                  <a href="#" className="text-[var(--text-muted)] hover:text-[#3B82F6] transition-colors duration-300">
                    {link}
                  </a>
                  {i < 2 && <span className="text-[var(--text-muted)] opacity-30">|</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
